
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {XHRFactory} from "../../XHRFactory.js";

export class EptBinaryLoader {
	extension() {
		return '.bin';
	}

	workerPath() {
		return Potree.scriptPath + '/workers/EptBinaryDecoderWorker.js';
	}

	load(node) {
		if (node.loaded) return;

		// COMPAT(legacy-ept): support both copc.js Key API and array-based keys.
		const keyToString = (key) => {
			if (window.Copc && window.Copc.Key) {
				return window.Copc.Key.toString(key);
			}
			if (Array.isArray(key)) {
				return key.join('-');
			}
			return String(key);
		};

		let url;
		// COMPAT(legacy-ept): support both old node.url() and new owner/base layout.
		if (typeof node.url === 'function') {
			url = node.url() + this.extension();
		} else {
			url = `${node.owner.base}/ept-data/${keyToString(node.key)}${this.extension()}`;
		}

		let xhr = XHRFactory.createXMLHttpRequest();
		xhr.open('GET', url, true);
		xhr.responseType = 'arraybuffer';
		xhr.overrideMimeType('text/plain; charset=x-user-defined');
		xhr.onreadystatechange = () => {
			if (xhr.readyState === 4) {
				if (xhr.status === 200 || xhr.status === 0) {
					let buffer = xhr.response;
					if (!(buffer instanceof ArrayBuffer)) {
						console.log('Invalid binary response for ' + url);
						node.loading = false;
						Potree.numNodesLoading--;
						return;
					}
					this.parse(node, buffer);
				} else {
					console.log('Failed ' + url + ': ' + xhr.status);
					node.loading = false;
					Potree.numNodesLoading--;
				}
			}
		};

		try {
			xhr.send(null);
		}
		catch (e) {
			console.log('Failed request: ' + e);
		}
	}

	parse(node, buffer) {
		let workerPath = this.workerPath();
		let worker = Potree.workerPool.getWorker(workerPath);

		worker.onmessage = function(e) {
			let g = new THREE.BufferGeometry();
			let numPoints = e.data.numPoints;

			let position = new Float32Array(e.data.position);
			g.setAttribute('position', new THREE.BufferAttribute(position, 3));

			let indices = new Uint8Array(e.data.indices);
			g.setAttribute('indices', new THREE.BufferAttribute(indices, 4));

			if (e.data.color) {
				let color = new Uint8Array(e.data.color);
				g.setAttribute('rgba', new THREE.BufferAttribute(color, 4, true));
			}
			if (e.data.intensity) {
				let intensity = new Float32Array(e.data.intensity);
				g.setAttribute('intensity',
						new THREE.BufferAttribute(intensity, 1));
			}
			if (e.data.classification) {
				let classification = new Uint8Array(e.data.classification);
				g.setAttribute('classification',
						new THREE.BufferAttribute(classification, 1));
			}
			if (e.data.returnNumber) {
				let returnNumber = new Uint8Array(e.data.returnNumber);
				g.setAttribute('return number',
						new THREE.BufferAttribute(returnNumber, 1));
			}
			if (e.data.numberOfReturns) {
				let numberOfReturns = new Uint8Array(e.data.numberOfReturns);
				g.setAttribute('number of returns',
						new THREE.BufferAttribute(numberOfReturns, 1));
			}
			if (e.data.pointSourceId) {
				let pointSourceId = new Uint16Array(e.data.pointSourceId);
				g.setAttribute('source id',
						new THREE.BufferAttribute(pointSourceId, 1));
			}

			g.attributes.indices.normalized = true;

			let tightBoundingBox = new THREE.Box3(
				new THREE.Vector3().fromArray(e.data.tightBoundingBox.min),
				new THREE.Vector3().fromArray(e.data.tightBoundingBox.max)
			);

			node.doneLoading(
					g,
					tightBoundingBox,
					numPoints,
					new THREE.Vector3(...e.data.mean));

			Potree.workerPool.returnWorker(workerPath, worker);
		};

		let toArray = (v) => [v.x, v.y, v.z];
		// COMPAT(legacy-ept): accept both legacy node.ept and owner.ept fields.
		const ept = node.ept || node.owner.ept;
		const scaleSource = ept.eptScale || ept.scale || [1, 1, 1];
		const offsetSource = ept.eptOffset || ept.offset || [0, 0, 0];
		const toXYZ = (v) => Array.isArray(v)
			? { x: v[0], y: v[1], z: v[2] }
			: v;
		// COMPAT(legacy-ept): support old key.b.min and new node.bounds formats.
		const mins = (node.key && node.key.b && node.key.b.min)
			? toArray(node.key.b.min)
			: node.bounds.slice(0, 3);

		let message = {
			buffer: buffer,
			schema: ept.schema,
			scale: toXYZ(scaleSource),
			offset: toXYZ(offsetSource),
			mins: mins
		};

		worker.postMessage(message, [message.buffer]);
	}
};

